import { Injectable, OnDestroy } from '@angular/core';
import { isEmpty } from 'lodash';
import {
  IPP_PORTAL_SESSION_BRIDGE_LOGOUT_KEY,
  IPP_SSO_LOGOUT_POST_MESSAGE,
} from '../constants/portal-session-bridge.constants';
import { environment } from '../../../environments/environment';

export const VMS_REGISTER_POST_MESSAGE = 'IPP_VMS_REGISTER';
/** Portal replies with sidebar logo URL and optional `portalAccessToken` (for `/vms/clear-access-cookie` when cookie is not sent cross-site). */
export const IPP_PORTAL_SIDEBAR_LOGO = 'IPP_PORTAL_SIDEBAR_LOGO';

function originFromEnvUrl(url: string | undefined): string | null {
  if (!url?.trim()) return null;
  try {
    return new URL(url.trim()).origin;
  } catch {
    return null;
  }
}

/** Mirrors `sidebar.component.ts` ngOnInit logo order, plus merchant upload URL when sidebar would fall back to default. */
function computePortalSidebarLogoUrlForDejaup(): string {
  const env = environment as {
    whitelabelLogoPath?: string;
    apiFullUrl?: string;
    defaultLogo?: string;
    portalMerchantLogoPath?: string;
  };
  const whitelabelBase = (env.whitelabelLogoPath || '').replace(/\/$/, '');
  const apiFull = (env.apiFullUrl || '').replace(/\/$/, '');
  const merchantBase = (env.portalMerchantLogoPath || '').replace(/\/$/, '');

  try {
    const localWhitelabelData = JSON.parse(localStorage.getItem('whitelabelResData') || 'null');
    const whitelabelLogo = localWhitelabelData?.logo_dashboard;
    if (!isEmpty(whitelabelLogo)) {
      const s = String(whitelabelLogo);
      if (/^https?:\/\//i.test(s)) {
        return s;
      }
      if (whitelabelBase) {
        return `${whitelabelBase}/${s}`;
      }
    }
  } catch {
    /* ignore */
  }

  try {
    const u = JSON.parse(localStorage.getItem('current_user') || 'null');
    const customerLogo = u?.customerLogo;
    if (!isEmpty(customerLogo) && apiFull) {
      return `${apiFull}/assets/images/${customerLogo}`;
    }
    const merchantLogo = u?.merchantLogo;
    if (!isEmpty(merchantLogo) && merchantBase) {
      return `${merchantBase}/${merchantLogo}`;
    }
  } catch {
    /* ignore */
  }

  return env.defaultLogo || '';
}

/** Same encrypted value as `Set-Cookie` / Portal `xaccesstoken` — lets DejaUp call `/vms/clear-access-cookie` when the cookie is not sent cross-site. */
function portalAccessTokenForDejaupBridge(): string | null {
  try {
    const u = JSON.parse(localStorage.getItem('current_user') || 'null');
    const t = u?.accessToken;
    return typeof t === 'string' && t.length > 0 ? t : null;
  } catch {
    return null;
  }
}

/**
 * DejaUp may `postMessage` {@link VMS_REGISTER_POST_MESSAGE} from `window.opener`; Portal replies with logo/token only.
 * Portal → DejaUp **logout**: (1) {@link postLogoutToTrackedDejaupWindows} to tabs opened from this app
 * (needed when third-party iframe storage is partitioned — hidden bridge `storage` alone is unreliable);
 * (2) {@link IPP_PORTAL_SESSION_BRIDGE_LOGOUT_KEY} for same-tab / legacy; (3) `storage` listeners on other
 * Portal tabs forward logout to DejaUp tabs they opened.
 */
@Injectable({ providedIn: 'root' })
export class SubAppWindowBridgeService implements OnDestroy {
  private readonly dejaUpWindows = new Set<Window>();
  private readonly onMessage = (e: MessageEvent) => this.replyToDejaupRegistration(e);
  private readonly onStorage = (e: StorageEvent) => this.onSiblingPortalTabBridgeStorage(e);

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', this.onMessage);
      window.addEventListener('storage', this.onStorage);
    }
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.onMessage);
      window.removeEventListener('storage', this.onStorage);
    }
  }

  /** Register a DejaUp tab opened via {@link window.open} (e.g. sidebar) so Portal can `postMessage` logout. */
  registerDejaupWindow(win: Window | null | undefined): void {
    if (typeof window === 'undefined' || !win || win.closed) return;
    this.pruneClosedDejaupWindows();
    this.dejaUpWindows.add(win);
  }

  private pruneClosedDejaupWindows(): void {
    for (const w of this.dejaUpWindows) {
      if (w.closed) this.dejaUpWindows.delete(w);
    }
  }

  private dejaupTargetOrigin(): string | null {
    return originFromEnvUrl((environment as { vipUrl?: string }).vipUrl);
  }

  private onSiblingPortalTabBridgeStorage(e: StorageEvent): void {
    if (e.storageArea !== window.localStorage) return;
    if (e.key !== IPP_PORTAL_SESSION_BRIDGE_LOGOUT_KEY || e.newValue == null || e.newValue === '') return;
    this.postLogoutToTrackedDejaupWindows();
  }

  /**
   * Sends {@link IPP_SSO_LOGOUT_POST_MESSAGE} to every non-closed DejaUp window we opened from this Portal tab.
   */
  postLogoutToTrackedDejaupWindows(): void {
    const targetOrigin = this.dejaupTargetOrigin();
    if (!targetOrigin) return;
    this.pruneClosedDejaupWindows();
    const payload = {
      type: IPP_SSO_LOGOUT_POST_MESSAGE,
      source: 'ipp-portal-parent',
      ts: Date.now(),
    };
    for (const w of this.dejaUpWindows) {
      if (w.closed) continue;
      try {
        w.postMessage(payload, targetOrigin);
      } catch {
        /* ignore */
      }
    }
  }

  private replyToDejaupRegistration(e: MessageEvent): void {
    if (e.data?.type !== VMS_REGISTER_POST_MESSAGE) return;
    const expected = originFromEnvUrl((environment as { vipUrl?: string }).vipUrl);
    if (!expected || e.origin !== expected || !e.source) return;
    const sidebarLogoUrl = computePortalSidebarLogoUrlForDejaup();
    const portalAccessToken = portalAccessTokenForDejaupBridge();
    if (!sidebarLogoUrl && !portalAccessToken) {
      return;
    }
    try {
      const payload: {
        type: typeof IPP_PORTAL_SIDEBAR_LOGO;
        ts: number;
        sidebarLogoUrl?: string;
        portalAccessToken?: string;
      } = { type: IPP_PORTAL_SIDEBAR_LOGO, ts: Date.now() };
      if (sidebarLogoUrl) {
        payload.sidebarLogoUrl = sidebarLogoUrl;
      }
      if (portalAccessToken) {
        payload.portalAccessToken = portalAccessToken;
      }
      (e.source as Window).postMessage(payload, e.origin);
    } catch {
      /* ignore */
    }
  }

  /**
   * Signals DejaUp to log out: parent `postMessage` (works with storage-partitioned iframe bridge) plus
   * `localStorage` so other Portal tabs can forward to DejaUp tabs they opened.
   */
  broadcastLogoutToVmsWindows(): void {
    this.postLogoutToTrackedDejaupWindows();
    try {
      localStorage.setItem(IPP_PORTAL_SESSION_BRIDGE_LOGOUT_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  }
}
